import { AbstractBluetoothAdapter } from '@services/bluetooth/adapter/AbstractBluetoothAdapter';
import { BluetoothUnknownErrorException, InvalidMacAddressException, MissingArgumentsException } from '@services/bluetooth/BluetoothExceptions';
import { VelocityBaseAdapter } from '../../shared/VelocityBaseAdapter';

export class VelocityBluetoothAdapter extends AbstractBluetoothAdapter {
    private velocityAdapter = new (class extends VelocityBaseAdapter {
        protected createTimeoutError(): Error {
            return new BluetoothUnknownErrorException({ message: 'Velocity call timed out' });
        }
    })();

    public pair(config: any): Promise<any> {
        console.log('VelocityBluetoothAdapter pair config ' + JSON.stringify(config));

        const macAddress = config[0]?.macAddress;
        if (macAddress === undefined) {
            return Promise.reject(new InvalidMacAddressException({ message: 'macAddress is undefined' }));
        }

        return this.velocityAdapter.callVelocityFunction('pairViaBT', macAddress).then((result: any) => this.handleVelocityResult(result));
    }

    public connect(config: any): Promise<any> {
        console.log('VelocityBluetoothAdapter connect config ' + JSON.stringify(config));
        // No connect actions are needed for velocity printing, function will no-op
        return Promise.resolve();
    }

    public clearAllLastPairedDevices(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public clearLastPairedDevice(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public getDeviceInfo(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public getLastPairedDevice(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public pairAndConnect(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public setLastPairedDevice(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public changeCharsetSerialComm(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public connectBle(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public disconnectBle(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public disconnectSerialComm(config: any): Promise<any> {
        console.log('VelocityBluetoothAdapter disconnect config ' + JSON.stringify(config));
        return this.velocityAdapter.callVelocityFunction('disconnectPrinter').then((result: any) => this.handleVelocityResult(result));
    }

    public readCharacteristicBle(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public requestDataSerialComm(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public writeCharacteristicBle(config: any): Promise<any> {
        return Promise.reject(new Error('Method not implemented'));
    }

    public writeDataSerialComm(config: any): Promise<any> {
        console.log('VelocityBluetoothAdapter writeDataSerialComm config ' + JSON.stringify(config));

        const macAddress = config[0]?.macAddress;
        if (macAddress === undefined) {
            return Promise.reject(new InvalidMacAddressException({ message: 'macAddress is undefined' }));
        }

        const zplPayload = config[0]?.data;
        if (zplPayload === undefined) {
            return Promise.reject(new MissingArgumentsException({ message: 'zplPayload is undefined' }));
        }

        // Convert non-ASCII characters (e.g. Japanese) to ZPL hex-encoded UTF-8.
        // This produces pure ASCII that passes safely through wls: URL and Velocity session.js,
        // and the printer natively decodes the hex bytes back to characters.
        const encodedZpl = VelocityBaseAdapter.encodeNonAsciiForZpl(zplPayload);

        return this.velocityAdapter.callVelocityFunction('printViaBT', encodedZpl, macAddress).then((result: any) => this.handleVelocityResult(result));
    }

    public getPrinterDPI(config: any): Promise<any> {
        const macAddress = config[0]?.macAddress;
        if (macAddress === undefined) {
            return Promise.reject(new InvalidMacAddressException({ message: 'macAddress is undefined' }));
        }

        return this.velocityAdapter.callVelocityFunction('getPrinterDPIViaBT', macAddress).then((result: any) => {
            if (result?.result < 0) {
                const dpiValue = result?.extra?.[0]?.value;
                const dpi = parseInt(dpiValue, 10);
                if (isNaN(dpi)) {
                    return Promise.reject(new BluetoothUnknownErrorException({ message: `Invalid DPI value: ${dpiValue}` }));
                }
                return { dpi };
            }
            return Promise.reject(new BluetoothUnknownErrorException({ message: JSON.stringify(result) }));
        });
    }

    /**
     * Handles the result from a Velocity function call.
     *
     * @param result - Result object from Velocity function
     * @returns Promise that resolves on success or rejects with appropriate exception
     */
    private handleVelocityResult(result: any): Promise<void> {
        /**
         * Velocity function returns a response in the format
         * {result: -1, extra: [{name: "message", value: "PAIR_SUCCESS", type: "string"}]}
         *
         * negative number means success, positive or zero means failure.
         */
        if (result?.result < 0) {
            return Promise.resolve();
        }
        return Promise.reject(new BluetoothUnknownErrorException({ message: JSON.stringify(result) }));
    }
}
